/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

/* /*!
 * Status: under construction
 * Contributor: [ ca-modeller, Jason Sheng]
 * Name: Button.js
 */
/*! */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createIcon } from '@ba-ui-toolkit/svgicon';
import TruncatedText from '@ba-ui-toolkit/truncatedtext';
import { Bidi, propsUtils } from '@ba-ui-toolkit/utils';
import { Button as CarbonButton } from 'carbon';

const { BaPropTypes } = propsUtils;

export default class Button extends Component {
	static propTypes = {
		active: BaPropTypes.deprecated('is ignored and not applicable to carbon button'),
		autofocus: PropTypes.bool,
		button: BaPropTypes.deprecated('is ignored and not applicable to carbon button'),
		children: PropTypes.node,
		className: PropTypes.string,
		dir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		disabled: PropTypes.bool,
		ellipsis: PropTypes.bool,
		fullWidth: PropTypes.bool,
		hidden: PropTypes.bool,
		hover: BaPropTypes.deprecated('is ignored and not applicable to carbon button'),
		icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		iconLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		iconRight: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		iconSize: PropTypes.oneOf(['small', 'normal', 'large']),
		intent: PropTypes.oneOf([
			'',
			'primary',
			'selection',
			'positive',
			'danger',
			'warning',
			'info',
		]),
		label: PropTypes.string,
		numShape: PropTypes.bool,
		onClick: PropTypes.func,
		pageNumShape: PropTypes.bool,
		set: PropTypes.oneOf(['primary', 'secondary']),
		size: PropTypes.oneOf(['normal', 'small']),
		style: PropTypes.object,
		tabIndex: PropTypes.oneOf([0, -1]),
		textAlign: PropTypes.oneOf(['left', 'right', 'center']),
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		title: PropTypes.string,
		variant: PropTypes.oneOf([
			'solid',
			'icon',
			'frame',
			'frameless',
			'bar',
			'barOff',
			'inline',
		]),
	};

	static defaultProps = {
		autofocus: undefined,
		set: 'primary',
		variant: 'solid',
		textAlign: 'center',
		size: 'normal',
		iconSize: 'small',
		dir: '',
		textDir: '',
		style: {},
	};

	_ref = node => {
		this._node = node;
	};

	focus() {
		this._node.focus();
	}

	render() {
		const {
			autofocus,
			children,
			className,
			disabled,
			ellipsis,
			fullWidth,
			hidden,
			icon,
			iconLeft,
			iconRight,
			iconSize,
			intent,
			label,
			set,
			size,
			style,
			tabIndex,
			textAlign,
			textDir,
			title,
			variant,
			...others
		} = this.props;

		const overrideStyles = {
			...(textAlign !== 'center' ? { textAlign, ...style } : {}),
			...style,
			...(fullWidth ? { minWidth: '100%' } : {}),
			...(ellipsis ? { maxWidth: '100%' } : {}),
			...(hidden ? { visibility: 'hidden' } : {}),
		};

		const hasLabel = label != null && label.length >= 0;
		const dir = Bidi.resolveDir(label, this.props.dir);
		const numShape = Bidi.resolveNumShape(this.props.pageNumShape, this.props.numShape);

		let labelElement;
		if (hasLabel) {
			const labelStr = Bidi.reshape(label, numShape);
			labelElement = labelStr;
			if ((fullWidth && ellipsis !== false) || ellipsis) {
				labelElement = <TruncatedText value={labelStr} textDir={textDir} />;
			}
		}

		const bProps = {
			...others,
			title,
			tabIndex: disabled ? -1 : tabIndex,
			className: `${className}`,
			disabled,
			autofocus: autofocus ? true : undefined,
			dir: dir !== '' ? dir : null,
			style: overrideStyles,
		};

		// Mapping to Carbon props
		let kind = set;

		if (variant === 'icon' || variant === 'inline' || variant === 'frameless') {
			kind = 'ghost';
			bProps.style = { ...bProps.style, border: '0', padding: '0', minHeight: '0' };
		} else if (intent === 'danger') {
			kind = set === 'primary' ? 'danger--primary' : 'danger';
		} else if (variant === 'barOff') {
			kind = 'ghost';
			bProps.style = { ...bProps.style, padding: '1px 7px 2px' };
		}

		return (
			<ButtonNode
				{...bProps}
				renderIcon={props =>
					createIcon(icon || iconRight || iconLeft, iconSize, null, null, props)
				}
				kind={kind}
				size={size === 'small' ? 'small' : 'default'}
				_ref={this._ref}
			>
				{labelElement}
				{children}
			</ButtonNode>
		);
	}
}

export class ButtonNode extends Component {
	static propTypes = {
		_ref: PropTypes.any,
	};

	render() {
		const { _ref, ...others } = this.props;

		return (
			<CarbonButton
				{...others} // used internally for Button
				ref={node => {
					this._node = node;
					_ref(node);
				}}
			/>
		);
	}
}
